import { useJDPolicies, useJDQuestions } from "@/api/jd-question-queries";
import { useJDSettings, type JDDetail } from "@/api/jd-queries";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/input";
import { Empty, ErrorNote, Panel, PanelHeader } from "@/components/ui/panel";
import { useDraft } from "@/lib/jd-draft-state";
import { importanceWeights, stackSplit } from "@/lib/jd-stack-split";
import { bucketLabel, byBucket, countsFor, DEFAULT_COUNTS, jdShare } from "@/lib/jd-steps";
import { cn } from "@/lib/utils";

/**
 * Step 4, Interview mix — DESIGN §20.9, §20.17a. Two plain questions: how many questions an interview
 * for this JD asks, and where they come from. The first used to sit inside the pick preview, where
 * nothing said the numbers were saved with the JD; now it is an explicit choice between ipp-platform's
 * numbers and the JD's own. Choosing the policy is the Resource Manager's; what a policy says is edited
 * on Config, because it changes every JD that uses it (§20.10). The technical part names how many
 * questions each of the JD's stacks gets, which a what-if counter on Skills used to show apart from
 * the numbers that are actually saved.
 */

const BASIS: Record<string, string> = {
  jd_min: "the JD's minimum experience",
  member: "the member's own experience",
  higher: "whichever is higher, the JD's minimum or the member's",
};

const clamp = (n: number) => Math.max(0, Math.min(20, Math.floor(n) || 0));

export function MixStep({ jd, canManage }: { jd: JDDetail; canManage: boolean }) {
  const questions = useJDQuestions(jd.jd.id);
  const policies = useJDPolicies();
  const settings = useJDSettings();
  const draft = useDraft();

  const buckets = byBucket(questions.data?.buckets ?? [], (b) => b.bucket);
  const saved = jd.jd.question_counts ?? null;
  const counts = draft.questionCounts(saved);
  const fixed = counts !== null;
  const shown = countsFor(
    buckets.map((b) => b.bucket),
    counts,
  );
  const fallback = policies.data?.find((p) => p.is_default);
  const policyId = draft.policyId(jd.jd.policy_id ?? null);
  // Unsaved skill edits included, so a change of importance on Skills shows here before Save.
  const skills = jd.skills.map(draft.skill);
  const weights = importanceWeights(settings.data?.values.jd_importance_weights);
  const basis = settings.data?.experience_basis ?? "jd_min";

  function setCount(bucket: string, n: number) {
    draft.setQuestionCounts({ ...shown, [bucket]: clamp(n) }, saved);
  }

  function relatedFrom(bucket: string, n: number) {
    const split = bucket === "technical" ? stackSplit(n, skills, weights) : [];
    if (split.length > 0)
      return (
        <>
          from its stacks (
          {split.map(([stack, k], i) => (
            <span key={stack}>
              {i > 0 && " · "}
              <span className="font-mono text-[11.5px]">{stack}</span> {k}
            </span>
          ))}
          )
        </>
      );
    const where = bucket === "project_domain" && jd.jd.domains.length ? jd.jd.domains.join(" · ") : null;
    return where ? (
      <>
        from <span className="font-mono text-[11.5px]">{where}</span>
      </>
    ) : (
      "from the general bank"
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[13px]">
        How a member's interview for this JD is put together.{" "}
        <span className="text-bank-muted">
          The defaults work — change these only when the client needs a different mix.
        </span>
      </p>

      <Panel>
        <PanelHeader title="How many questions per interview" />
        <fieldset disabled={!canManage}>
          <div
            role="radiogroup"
            aria-label="Who decides how many questions"
            className="flex flex-wrap gap-2.5 px-4 pt-3.5 pb-2"
          >
            {(
              [
                [false, "Let ipp-platform decide", "It sends its own numbers when a member starts."],
                [
                  true,
                  "Fixed for this JD",
                  "Saved with the JD. ipp-platform uses these unless it asks for other numbers.",
                ],
              ] as const
            ).map(([value, title, text]) => (
              <label
                key={title}
                className={cn(
                  "flex flex-[1_1_240px] cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2.5",
                  fixed === value ? "border-bank-accent bg-bank-accent-wash" : "border-bank-border-strong",
                )}
              >
                <input
                  type="radio"
                  name="jd-count-mode"
                  className="accent-bank-accent mt-1"
                  checked={fixed === value}
                  onChange={() =>
                    draft.setQuestionCounts(
                      value
                        ? countsFor(
                            buckets.map((b) => b.bucket),
                            null,
                          )
                        : null,
                      saved,
                    )
                  }
                />
                <span>
                  <span className="block text-[13.5px] font-semibold">{title}</span>
                  <span className="text-bank-muted text-[12.5px]">{text}</span>
                </span>
              </label>
            ))}
          </div>
          {buckets.length === 0 ? (
            <Empty>Loading the buckets…</Empty>
          ) : (
            <div className="pt-1">
              {buckets.map(({ bucket }) => (
                <div
                  key={bucket}
                  className="border-bank-border grid grid-cols-[200px_140px_minmax(0,1fr)] items-center gap-4 border-t px-4 py-2.5"
                >
                  <span>
                    <span className="block text-[13.5px] font-semibold">{bucketLabel(bucket)}</span>
                    <span className="text-bank-muted font-mono text-[11px]">{bucket}</span>
                  </span>
                  <span
                    className={cn(
                      "border-bank-border-strong bg-bank-surface inline-flex w-fit items-center rounded-md border",
                      !fixed && "opacity-60",
                    )}
                  >
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Fewer ${bucketLabel(bucket)} questions`}
                      disabled={!fixed}
                      onClick={() => setCount(bucket, (shown[bucket] ?? 0) - 1)}
                    >
                      −
                    </Button>
                    <span className="w-8 text-center font-mono text-[13px] tabular-nums">
                      {shown[bucket] ?? 0}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`More ${bucketLabel(bucket)} questions`}
                      disabled={!fixed}
                      onClick={() => setCount(bucket, (shown[bucket] ?? 0) + 1)}
                    >
                      +
                    </Button>
                  </span>
                  <span className="text-bank-muted text-[12.5px]">
                    {bucket === "technical"
                      ? "Always asked — ipp-platform never lets a member leave this out."
                      : ""}
                    {!fixed && (DEFAULT_COUNTS[bucket] ?? 0) > 0 && bucket !== "technical"
                      ? "The usual mix."
                      : ""}
                  </span>
                </div>
              ))}
              <div className="border-bank-border bg-bank-surface-2 grid grid-cols-[200px_140px_minmax(0,1fr)] items-center gap-4 rounded-b-lg border-t px-4 py-2.5">
                <span className="text-[13.5px] font-semibold">Total</span>
                <span className="pl-9 font-mono text-[13px] font-semibold tabular-nums">
                  {Object.values(shown).reduce((n, c) => n + c, 0)}
                </span>
                <span className="text-bank-muted text-xs">
                  0 to 20 each. Every bucket at 0 goes back to "Let ipp-platform decide".
                </span>
              </div>
            </div>
          )}
        </fieldset>
      </Panel>

      <Panel className="overflow-hidden">
        <PanelHeader
          title="Where the questions come from"
          actions={
            <span className="text-bank-muted text-xs">This JD's own questions first, then related ones</span>
          }
        />
        <div className="flex flex-wrap items-end gap-3 px-4 py-3.5">
          <div className="flex-[0_1_340px]">
            <label htmlFor="jd-policy" className="mb-1 block text-[12.5px] font-medium">
              Pick policy
            </label>
            <Select
              id="jd-policy"
              disabled={!canManage}
              value={policyId === null ? "" : String(policyId)}
              onChange={(e) =>
                draft.setPolicy(
                  e.target.value === "" ? null : Number(e.target.value),
                  jd.jd.policy_id ?? null,
                )
              }
            >
              <option value="">Follow the default{fallback ? ` (${fallback.name})` : ""}</option>
              {(policies.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.is_default ? " · default" : ""}
                </option>
              ))}
            </Select>
          </div>
          <p className="text-bank-muted mb-1.5 flex-[1_1_300px] text-[12.5px]">
            A policy's percentages are edited on Config › Job descriptions; a change there reaches every JD
            that uses it.
          </p>
        </div>
        <ErrorNote error={policies.error} />
        {/* The shares come from the API, so they are the saved policy's until Save. */}
        {policyId !== (jd.jd.policy_id ?? null) && (
          <p className="text-bank-attention-ink px-4 pb-2.5 text-xs">
            The shares below change to the new policy's once you save.
          </p>
        )}
        <div className="bg-bank-surface-2 text-bank-muted border-bank-border grid grid-cols-[180px_minmax(220px,1fr)_120px] gap-4 border-t px-4 py-2 font-mono text-[10.5px] tracking-[0.07em] uppercase">
          <span>Bucket</span>
          <span>Share of each interview</span>
          <span className="text-right">JD questions now</span>
        </div>
        {buckets.map((b) => {
          const want = shown[b.bucket] ?? 0;
          const fromJD = jdShare(want, b.percent, b.served);
          return (
            <div
              key={b.bucket}
              className="border-bank-border grid grid-cols-[180px_minmax(220px,1fr)_120px] items-start gap-4 border-t px-4 py-3"
            >
              <span className="text-[13.5px] font-semibold">{bucketLabel(b.bucket)}</span>
              <div>
                <div className="flex h-5 gap-0.5 overflow-hidden rounded font-mono text-[11px]">
                  {b.percent > 0 && (
                    <span
                      style={{ flex: b.percent }}
                      className="bg-bank-accent-strong text-bank-on-accent flex items-center truncate px-2"
                    >
                      This JD's own {b.percent}%
                    </span>
                  )}
                  {b.percent < 100 && (
                    <span
                      style={{ flex: 100 - b.percent }}
                      className="bg-bank-surface-3 text-bank-inert-ink flex items-center truncate px-2"
                    >
                      related {100 - b.percent}%
                    </span>
                  )}
                </div>
                <p className="mt-1.5 text-[12.5px]">
                  {want === 0 ? (
                    <span className="text-bank-muted">Not asked — 0 questions in this part.</span>
                  ) : (
                    <>
                      {want} question{want === 1 ? "" : "s"} →{" "}
                      {fromJD > 0 && <strong>{fromJD === want ? "all" : fromJD} from this JD's own</strong>}
                      {fromJD > 0 && want - fromJD > 0 && ", "}
                      {want - fromJD > 0 && (
                        <>
                          {fromJD > 0 ? `${want - fromJD} ` : "all "}
                          {relatedFrom(b.bucket, want - fromJD)}
                        </>
                      )}
                    </>
                  )}
                  {b.percent > 0 && b.served < Math.floor((want * b.percent) / 100) && (
                    <span className="text-bank-muted">
                      {" "}
                      (it has only {b.served} here; the rest come from related ones)
                    </span>
                  )}
                </p>
                {b.percent === 0 && b.served > 0 && (
                  <p className="text-bank-attention-ink mt-1 text-[12.5px]">
                    {b.served} JD {b.served === 1 ? "question" : "questions"} here, unused at 0%.
                  </p>
                )}
              </div>
              <span
                className={cn(
                  "text-right font-mono text-[12.5px] tabular-nums",
                  b.served === 0 && "text-bank-muted",
                )}
              >
                {b.served}
              </span>
            </div>
          );
        })}
      </Panel>

      <div className="flex flex-wrap items-start gap-3">
        <Panel className="flex-[1_1_300px]">
          <PanelHeader title="Difficulty" />
          <p className="p-4 text-[13px]">
            Pitched at <strong>{BASIS[basis] ?? BASIS.jd_min}</strong>
            {jd.jd.experience_min !== null && basis !== "member"
              ? ` — ${jd.jd.experience_min} years here`
              : ""}
            . <span className="text-bank-muted">Which figure it uses is set on Config.</span>
          </p>
        </Panel>
        <details className="border-bank-border bg-bank-surface flex-[2_1_420px] rounded-lg border">
          <summary className="cursor-pointer px-4 py-2.5 text-sm font-semibold">How picking works</summary>
          <div className="grid gap-4 px-4 pb-4 text-[13px] sm:grid-cols-3">
            <div>
              <p className="text-bank-muted mb-1 font-mono text-[10.5px] tracking-[0.07em] uppercase">
                Within this JD's share
              </p>
              Pinned questions first, then its own before matched ones, Must skills before Nice — varied, like
              every pick.
            </div>
            <div>
              <p className="text-bank-muted mb-1 font-mono text-[10.5px] tracking-[0.07em] uppercase">
                Rounding
              </p>
              Rounded down, but never to none while the JD has a question in that bucket. Whatever it can't
              fill, the related share does.
            </div>
            <div>
              <p className="text-bank-muted mb-1 font-mono text-[10.5px] tracking-[0.07em] uppercase">
                Between the JD's stacks
              </p>
              {/* §20.9: core/jd.py:stack_weights, the weights from jd_importance_weights. */}
              Each technical skill adds weight to its stack — Must {weights.must ?? 0}, Nice{" "}
              {weights.nice ?? 0}, Other {weights.other ?? 0} — and the related questions are shared out by
              weight.
            </div>
          </div>
        </details>
      </div>
    </div>
  );
}
